import React, { Suspense, useEffect, useState } from 'react';
import {
  getWaitingApproveStores, openForBulkSaleStore,
  openForSaleStore
} from '../../../../store/requests/definitions';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import { useTranslation } from 'react-i18next';
import { STATUS } from '../../../../helpers/constants';
import {
  Col,
  ConfigProvider,
  Row,
  Table,
  Typography,
  Tooltip,
  Button,
  Badge,
  Popconfirm,
  notification,
  Tag,
  Form,
  Divider
} from 'antd';
import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  FileSyncOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import trTR from 'antd/es/locale/tr_TR';
import moment from 'moment/moment';
import { useSelector } from 'react-redux';
const { Text } = Typography;
const WaitingApproveStoresFilter = React.lazy(
  () => import('./WaitingApproveStoresFilters'),
);
const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 30, 50],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};

const WaitingApproveStores = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [filters, setFilters] = useState({});
  const [waitingApproveStoreData, setWaitingApproveStoreData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState(initialPageParams);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [form] = Form.useForm();
  const activeStores = useSelector((state) => state.definitions.userStores);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    setIsFiltersOpen(false);
    fetchData(initialPageParams);
    return () => {
      setLoading(false);
    };
  }, [filters]);

  const fetchData = (tableParamsValue) => {
    const apiParams = buildApiParams(tableParamsValue);
    getStockTransactions(apiParams);
  };

  const buildApiParams = (tableParamsValue) => {
    let queryParams = {};

    const { region, ...rest } = filters;

    if (Object.values(rest).some((item) => !!item)) {
      queryParams = Object.entries(rest)
        .filter(
          ([item, value]) => value != null && value !== undefined && rest[item],
        )
        .reduce((acc, [item, value]) => {
          acc[item] = encodeURI(value);
          return acc;
        }, {});
    }

    queryParams.page = tableParamsValue.pagination.current - 1;
    queryParams.size = tableParamsValue.pagination.pageSize;

    if (tableParamsValue.column) {
      queryParams.sortField = `${tableParamsValue.column.dataIndex}`;
      queryParams.descending = tableParamsValue.order === 'descend';
    }

    return queryParams;
  };

  const getStockTransactions = (apiParams) => {
    setSelectedRowKeys([]);
    if (!loading) {
      setLoading(true);
      getWaitingApproveStores(apiParams)
        .then((stores) => {
          const { data, totalElements, currentPage, currentSize } = stores;
          setWaitingApproveStoreData(data);
          setTableParams({
            pagination: {
              total: totalElements,
              current: currentPage + 1,
              pageSize: currentSize,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const getToolTipCheckbox = (defaultChecked, content, defaultOpen = false) => {
    return (
      <div>
        <Tooltip
          color="#0751a0"
          placement="bottom"
          title={(defaultChecked || defaultOpen) && content}
        >
          <Badge
            showZero
            count={<InfoCircleOutlined style={{ color: '#1890ff' }} />}
          >
            {defaultChecked ? (
              <CheckCircleTwoTone
                style={{ fontSize: 20 }}
                twoToneColor="#52c41a"
              />
            ) : (
              <CloseCircleTwoTone
                style={{ fontSize: 20 }}
                twoToneColor="#ff4d4f"
              />
            )}
          </Badge>
        </Tooltip>
      </div>
    );
  };

  const openForSaleCheck = (record) => {
    const {
      zoneCreated,
      collectorCreated,
      courierCreated,
      availableSlots,
      paymentTypes,
      stockInformation,
      storeTypeCreated,
      productSkuOffersInformation,
      paymentTypeAddedAlongWithAvailablePaymentOnDelivery,
      availablePaymentOnDelivery,
      zones,
    } = record;
    const isPaymentTypesValid = availablePaymentOnDelivery ? paymentTypes.length > 0 : true;
    return !(
      zoneCreated &&
      collectorCreated &&
      courierCreated &&
      availableSlots.length > 0 &&
      isPaymentTypesValid &&
      stockInformation?.count > 0 &&
      storeTypeCreated &&
      productSkuOffersInformation?.skuOfferCount > 0 &&
      paymentTypeAddedAlongWithAvailablePaymentOnDelivery &&
      zones?.every((zone) => zone?.shipmentTypes?.length > 0)
    );
  };

  const openSale = (record) => {
    openForSaleStore(record.id).then((res) => {
      notificationToast(
        STATUS.SUCCESS,
        t('processCompleted'),
        t('successOpenedForSale'),
      );
      fetchData(initialPageParams);
    });
  };

  const openForBulkSale = () => {
    if (selectedRowKeys.length > 0) {
      const payload = {
        storeIds: selectedRowKeys,
      };
      openForBulkSaleStore(payload).then((res) => {
        notificationToast(
          STATUS.SUCCESS,
          t('processCompleted'),
          t('successOpenedForSale'),
        );
        fetchData(initialPageParams);
      });
    }
  }

  const notificationToast = (type, message, description) => {
    notification[type]({
      message,
      description,
    });
  };

  const columns = [
    {
      title: t('store'),
      dataIndex: 'name',
      showSorterTooltip: false,
      sorter: true,
      render: (text, record) => (
        <div>{`${record?.code} - ${record?.name}`}</div>
      ),
      width: 300,
    },
    {
      title: t('ecomParameters'),
      showSorterTooltip: false,
      render: (text, record) => {
        const content = (
          <>
            {
              <div>
                <Text type="warning">{t('paymentType')}:</Text>{' '}
                {record?.paymentTypes?.map((item, idx) => {
                  return (
                    <div key={idx}>
                      <p>{item?.paymentType?.name}</p>
                    </div>
                  );
                })}
              </div>
            }
            <div>
              <Text type="warning">{t('alternativeProduct')}:</Text>{' '}
                {record?.allowAlternativeProduct ? t('yes') : t('no')}
            </div>
            <div>
              <Text type="warning">{t('suitableForPaymentAtDoor')}:</Text>{' '}
              {record?.availablePaymentOnDelivery === null ? '-' : record?.availablePaymentOnDelivery ?  t('yes') : t('no')}
            </div>
            <div>
              <Text type="warning">{t('collectionType')}:</Text>{' '}
              {t(record?.collectionType)}
            </div>
          </>
        );
        const isPaymentTypesValid = record?.availablePaymentOnDelivery ? record?.paymentTypes.length > 0 : true;
        return getToolTipCheckbox(
          (record?.paymentTypeAddedAlongWithAvailablePaymentOnDelivery && record?.allowAlternativeProduct !== null && record?.collectionType !== null && isPaymentTypesValid),
          content,
          true,
        );
      },
    },
    {
      title: t('zone'),
      showSorterTooltip: false,
      render: (text, record) => {
        const content = (
          <>
            {record?.zones.length ? record?.zones?.map((item, idx) => {
              return (
                <div key={idx}>
                  <p><Text type="warning"> {t('zone')}:</Text>
                    <div>{item.name}</div>
                  </p>
                  <p><Text type="warning">{t('shipmentType2')}:</Text>
                    <div> {item?.shipmentTypes?.map((item, idx) => item?.name).join(', ')}</div>
                  </p>
                  {(record?.zones.length - 1) !== idx && <Divider style={{ background: 'white' }} />}
                </div>
              );
            }) : '-'
            }
          </>
        );
        return getToolTipCheckbox((record?.zoneCreated && checkZoneShipmentType(record)), content, content);
      }
    },
    {
      title: t('slot'),
      showSorterTooltip: false,
      render: (text, record) => {
        const content = (
          <>
            {record?.availableSlots?.map((item, idx) => {
              return <div key={idx}>{item?.slotTime}</div>;
            })}
          </>
        );
        return getToolTipCheckbox(!!record?.availableSlots?.length, content);
      },
    },
    {
      title: t('collector'),
      showSorterTooltip: false,
      render: (text, record) => {
        const content = (
          <>
            {record?.collectors?.map((item, idx) => {
              return <div key={idx}>{item?.name}</div>;
            })}
          </>
        );
        return getToolTipCheckbox(record?.collectorCreated, content);
      },
    },
    {
      title: t('courier'),
      showSorterTooltip: false,
      render: (text, record) => {
        const content = (
          <>
            {record?.couriers?.map((item, idx) => {
              return <div key={idx}>{item?.name}</div>;
            })}
          </>
        );
        return getToolTipCheckbox(record?.courierCreated, content);
      },
    },
    {
      title: t('activeProduct'),
      showSorterTooltip: false,
      render: (text, record) =>
        record?.productSkuOffersInformation?.skuOfferCount,
    },
    {
      title: t('lastAndFirstUpdateProductDate'),
      showSorterTooltip: false,
      render: (text, record) => {
        const offersInfo = record?.productSkuOffersInformation;
        return offersInfo
          ? `${moment(offersInfo.firstPriceModifiedAt).format('DD-MM-YYYY')} - ${moment(offersInfo.lastPriceModifiedAt).format('DD-MM-YYYY')}`
          : '-';
      },
    },
    {
      title: t('countOfStockedProducts'),
      showSorterTooltip: false,
      render: (text, record) => record?.stockInformation?.count,
    },
    ,
    {
      title: t('stockUpdateDate'),
      showSorterTooltip: false,
      render: (text, record) =>
        record?.stockInformation?.lastUpdateTime
          ? moment(record?.stockInformation?.lastUpdateTime).format(
              'DD-MM-YYYY',
            )
          : '-',
    },
    {
      render: (text, record) => (
        <Popconfirm
          title={t('modalConfirm')}
          disabled={openForSaleCheck(record)}
          icon={
            <QuestionCircleOutlined
              style={{
                color: 'red',
              }}
            />
          }
          okText={t('yes')}
          cancelText={t('no')}
          onConfirm={() => openSale(record)}
        >
          <Button
            size="small"
            type="primary"
            disabled={openForSaleCheck(record)}
            icon={
              openForSaleCheck(record) ? (
                <SyncOutlined spin />
              ) : (
                <CheckCircleOutlined />
              )
            }
          >
            {t('openForSale')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const checkZoneShipmentType = (record) => {
    return record?.zones?.every((zone) => zone?.shipmentTypes?.length > 0);
  }

  const getStoreName = (storeCode) => {
    const currentStore = activeStores.find((item) => item.code === storeCode);
    return `${currentStore?.code} - ${currentStore?.name}`;
  };

  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      storeCode: 'store',
    };
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        const localizedField = fieldLocalizationVars[item] || item;
        const tagLabel = `${t(localizedField)} : ${item === 'storeCode' ? getStoreName(filters[item]) : t(`${filters[item]}`)}`;

        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {tagLabel}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, selectedRows) =>
      setSelectedRowKeys(newSelectedRowKeys),
    getCheckboxProps: (record) => ({
      disabled: openForSaleCheck(record),
      name: record.name,
    }),
  };

  return (
    <>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <Col>
          <Popconfirm
            title={t('modalConfirm')}
            disabled={!(selectedRowKeys.length > 0)}
            icon={
              <QuestionCircleOutlined
                style={{
                  color: 'red',
                }}
              />
            }
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={openForBulkSale}
          >
            <Tooltip title={t('availableForBulkSale')}>
              <Button
                type="primary"
                shape="circle"
                disabled={!(selectedRowKeys.length > 0)}
                icon={<FileSyncOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <ConfigProvider locale={trTR}>
        <Table
          style={{ width: '100%' }}
          size="small"
          columns={columns}
          dataSource={waitingApproveStoreData}
          pagination={{
            ...tableParams.pagination,
            showTotal: (total) => `${total} ${t('totalRecord')}`,
            pageSizeOptions: [20, 30, 50],
          }}
          rowSelection={rowSelection}
          loading={loading}
          onChange={(pagination, filters, sorter) => {
            fetchData({
              pagination,
              ...sorter,
            });
          }}
          rowKey={(record) => record?.id}
          scroll={{
            y: '70vh',
          }}
        />
      </ConfigProvider>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <WaitingApproveStoresFilter
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            activeStores={activeStores}
            form={form}
          />
        )}
      </Suspense>
    </>
  );
};
export default WaitingApproveStores;
